import useSharedScheduleRealtime from '../../realtime/useSharedScheduleRealtime';
import useCalendarHolidaysRealtime from '../../realtime/useCalendarHolidaysRealtime';
import useEngineeringCapacityRealtime from '../../realtime/useEngineeringCapacityRealtime';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useMsal } from '@azure/msal-react';
import TopNav from '../TopNav/TopNav';
import EngineeringCalendar from '../EngineeringPage/EngineeringCalendar';
import EngineeringScheduleOptimizer from './EngineeringScheduleOptimizer';
import EnhancedJobScheduleTable from './EnhancedJobScheduleTable';
import ReadyToSchedule from './ReadyToSchedule';
import ShippedJobs from './ShippedJobs';
import CanceledJobs from './CanceledJobs';
import OnHoldJobs from './OnHoldJobs';
import MoveRequests from './MoveRequests';
import PendingInPortal from './PendingInPortal';
import JobScheduleSearch from './JobScheduleSearch';
import GlobalJobSearch from './components/GlobalJobSearch';
import ReleasePendingCSO from './ReleasePendingCSO';
import FullReleaseStatusMismatch from './FullReleaseStatusMismatch';
import ScheduleChangeLog from './ScheduleChangeLog';
import { clearJobSchedule, fetchJobSchedule } from '../../redux/slices/jobScheduleSlice';
import { clearSellPrices } from '../../redux/slices/sellPriceSlice';
import { clearChangeOrderDetails } from '../../redux/slices/changeOrderDetailsSlice';
import { fetchDropdownValues } from '../../redux/slices/dropdownSlice';
import useCriticalData from '../../functions/useCriticalData';
import { useEngineeringTasks } from '../../functions/useEngineeringTasks';
import { Alert, Button } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { resetWeekData } from '../../redux/slices/weekDataSlice';
import { filterJobsGlobally, filterJobsByStatus, filterJobsForSchedule } from './utils/globalSearchUtils';
import useDebounce from './hooks/useDebounce';
import { clearJob } from '../../redux/slices/checkoutSlice';
import { clearJobId } from '../../redux/slices/jobIdSlice';
import { setPtoEntries } from '../../redux/slices/ptoSlice';
import { setRecords as setProtectedTimeRecords } from '../../redux/slices/protectedTimeSlice';
import { portalApi } from '../../redux/api/portalApi';
import { usePermissions } from '../../hooks/usePermissions';
import './JobScheduleResponsive.css';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  PRODUCTION_SCHEDULE_TAB_ROUTES,
  getProductionSchedulePathForTab,
  getProductionScheduleTabFromPath,
} from '../../config/productionRoutes';

const JobScheduleContainer = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  useEngineeringCapacityRealtime();
  const [searchTerm, setSearchTerm] = useState('');
  const loading = useSelector((state) => state.jobSchedule.loading);
  const error = useSelector((state) => state.jobSchedule.error);
  const jobScheduleData = useSelector((state) => state.jobSchedule.data);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const rolesReady = useSelector(state => state.user.status === 'succeeded');
  const urls = useSelector(state => state.apiUrls.urls);
  const initializedScope = useRef(null);
  const { canView, canEdit, isLoading: isPermissionsLoading } = usePermissions();

  // Load critical data including holidays and user dropdowns
  const { refreshApi } = useCriticalData({
    apiKeys: ['holidays'],
    includeUserDropdowns: true,
    autoLoad: true,
  });

  // Load engineering tasks
  const { fetchTasks } = useEngineeringTasks();

  // Debounce search term for performance
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Create filtered data for different component types
  const filteredData = useMemo(() => {
    if (!jobScheduleData || !Array.isArray(jobScheduleData)) {
      return {
        allJobs: [],
        scheduleJobs: [],
        shippedJobs: [],
        CanceledJobs: [],
        onHoldJobs: [],
      };
    }

    return {
      allJobs: filterJobsGlobally(jobScheduleData, debouncedSearchTerm),
      scheduleJobs: filterJobsForSchedule(jobScheduleData, debouncedSearchTerm),
      shippedJobs: filterJobsByStatus(jobScheduleData, debouncedSearchTerm, 'Shipped'),
      CanceledJobs: filterJobsByStatus(jobScheduleData, debouncedSearchTerm, 'canceled'),
      onHoldJobs: filterJobsByStatus(jobScheduleData, debouncedSearchTerm, 'On Hold'),
    };
  }, [jobScheduleData, debouncedSearchTerm]);

  const hasJobScheduleData = Array.isArray(jobScheduleData) && jobScheduleData.length > 0;
  const showInitialJobScheduleLoading = loading && !hasJobScheduleData;

  const visibleTabs = useMemo(
    () => PRODUCTION_SCHEDULE_TAB_ROUTES.filter((tab) => canView(tab.permissionKey)),
    [canView]
  );

  const requestedTab = getProductionScheduleTabFromPath(location.pathname);
  const activeTab = requestedTab && visibleTabs.some((tab) => tab.id === requestedTab)
    ? requestedTab
    : visibleTabs[0]?.id || '';

  const canEditAnyVisibleTab = useMemo(
    () => visibleTabs.some((tab) => canEdit(tab.permissionKey)),
    [canEdit, visibleTabs]
  );

  useSharedScheduleRealtime({ blocked: loading || isRefreshing || isPermissionsLoading });
  useCalendarHolidaysRealtime(!isPermissionsLoading);

  // PTO API functions
  const fetchPtoEntries = React.useCallback(async () => {
    try {
      const response = await dispatch(
        portalApi.endpoints.getPto.initiate(undefined, { forceRefetch: true, subscribe: false })
      ).unwrap();
      // Dispatch PTO data to Redux store for use in engineering components
      dispatch(setPtoEntries(response));
      return response;
    } catch (error) {
      console.error('Error fetching PTO entries:', error);
      throw error;
    }
  }, [dispatch]);
  
  // Protected Time API functions
  const fetchProtectedTimeRecords = React.useCallback(async () => {
    try {
      const response = await dispatch(
        portalApi.endpoints.getProtectedTime.initiate(undefined, { forceRefetch: true, subscribe: false })
      ).unwrap();
      // Dispatch Protected Time data to Redux store
      dispatch(setProtectedTimeRecords(response));
      return response;
    } catch (error) {
      console.error('Error fetching Protected Time records:', error);
      throw error;
    }
  }, [dispatch]);

  // Refresh function to clear and reload job schedule data
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Clear existing data first
      dispatch(clearJobSchedule());
      dispatch(clearSellPrices());
      dispatch(clearChangeOrderDetails());
      // Note: Don't clear dropdowns - useCriticalData manages caching and will refresh if needed
      
      // Fetch fresh data from the API
      await dispatch(fetchJobSchedule()).unwrap();
      
      // Fetch dropdown values (will be fresh since cache was cleared)
      dispatch(fetchDropdownValues());
      
      // Refresh holidays data
      await refreshApi('holidays');
      
      // Note: User dropdowns are managed by useCriticalData hook
      
      // Refresh PTO entries
      await fetchPtoEntries();
      
      // Refresh protected time records
      await fetchProtectedTimeRecords();
      
      // Refresh engineering tasks
      await fetchTasks();
      
      // Note: Holidays are managed by useCriticalData hook
      
      console.log('Job schedule data refreshed successfully');
    } catch (error) {
      console.error('Error refreshing job schedule data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Fetch all job data once when the component mounts
  useEffect(() => {
    const accountId = accounts[0]?.localAccountId;
    if (!accountId) { initializedScope.current = null; return; }
    if (!rolesReady || !urls.fullSchedule || !urls.engineeringTasks) return;
    const scope = JSON.stringify([accountId, urls.fullSchedule, urls.engineeringTasks]);
    if (initializedScope.current === scope) return;
    initializedScope.current = scope;
    // Fetch common data needed by all components
    dispatch(clearJob()); // Clear any existing job data
    dispatch(clearJobId()); // Clear job ID when the component mounts
    dispatch(resetWeekData());
    dispatch(clearJobSchedule());
    dispatch(clearSellPrices());
    dispatch(clearChangeOrderDetails());
    dispatch(fetchJobSchedule()); // Fetch all job data without filters
    dispatch(fetchDropdownValues());
    fetchTasks(); // Fetch engineering tasks
    fetchPtoEntries().catch(() => {}); // Error is logged by the loader.
    fetchProtectedTimeRecords().catch(() => {});

    // Note: User dropdowns and holidays are managed by useCriticalData hook
  }, [dispatch, accounts, rolesReady, urls.fullSchedule, urls.engineeringTasks, fetchTasks, fetchPtoEntries, fetchProtectedTimeRecords]);

  // Note: Holidays are now fetched via useCriticalData hook
  // Removed fetchHolidays function and related useEffects

  // Handle tab switching
  const handleTabChange = (tabName) => {
    if (visibleTabs.some((tab) => tab.id === tabName)) {
      navigate(getProductionSchedulePathForTab(tabName));
    }
  };

  // Keep the URL aligned to the first schedule tab the user can view.
  useEffect(() => {
    if (isPermissionsLoading || visibleTabs.length === 0 || !activeTab) {
      return;
    }

    const canonicalPath = getProductionSchedulePathForTab(activeTab);
    const currentPath = location.pathname.replace(/\/+$/, '').toLowerCase();
    if (canonicalPath && currentPath !== canonicalPath.toLowerCase()) {
      navigate(canonicalPath, { replace: true });
    }
  }, [
    activeTab,
    isPermissionsLoading,
    location.pathname,
    navigate,
    visibleTabs.length,
  ]);

  const renderContent = () => {
    switch (activeTab) {
      case 'engineeringCalendar':
        return <EngineeringCalendar canEdit={canEdit('schedule.engineering-schedule')} />;
      case 'engineeringOptimizer':
        return (
          <EngineeringScheduleOptimizer
            canEdit={canEdit('schedule.engineering-optimizer')}
            filteredJobs={filteredData.allJobs}
          />
        );
      case 'enhancedJobSchedule':
        return (
          <EnhancedJobScheduleTable
            canEdit={canEdit('schedule.job-schedule')}
            filteredJobs={filteredData.scheduleJobs}
          />
        );
      case 'readyToSchedule':
        return (
          <ReadyToSchedule
            canEdit={canEdit('schedule.ready-to-schedule')}
            filteredJobs={filteredData.allJobs}
          />
        );
      case 'releasePendingCSO':
        return (
          <ReleasePendingCSO
            canEdit={canEdit('schedule.release-pending-cso')}
            filteredJobs={filteredData.allJobs}
          />
        );
      case 'fullReleaseStatusMismatch':
        return (
          <FullReleaseStatusMismatch
            canEdit={canEdit('schedule.full-release-mismatch')}
            filteredJobs={filteredData.allJobs}
          />
        );
      case 'pendingInPortal':
        return (
          <PendingInPortal
            canEdit={canEdit('schedule.pending-in-portal')}
            filteredJobs={filteredData.allJobs}
          />
        );
      case 'shippedJobs':
        return (
          <ShippedJobs
            canEdit={canEdit('schedule.shipped-jobs')}
            filteredJobs={filteredData.shippedJobs}
            searchTerm={searchTerm}
          />
        );
      case 'CanceledJobs':
        return (
          <CanceledJobs
            canEdit={canEdit('schedule.canceled-jobs')}
            filteredJobs={filteredData.CanceledJobs}
          />
        );
      case 'onHoldJobs':
        return (
          <OnHoldJobs
            canEdit={canEdit('schedule.on-hold-jobs')}
            filteredJobs={filteredData.onHoldJobs}
          />
        );
      case 'moveRequests':
        return (
          <MoveRequests
            canEdit={canEdit('schedule.move-requests')}
            filteredJobs={filteredData.allJobs}
          />
        );
      case 'scheduleChangeLog':
        return <ScheduleChangeLog filteredJobs={filteredData.allJobs} />;
      case 'search':
        return (
          <JobScheduleSearch
            canEdit={canEdit('schedule.search')}
            filteredJobs={filteredData.allJobs}
          />
        );
      default:
        return null;
    }
  };
  return (
    <div className="job-schedule-page">
      <TopNav />
      {!isPermissionsLoading && !canEditAnyVisibleTab && visibleTabs.length > 0 && (
        <Alert variant="info" className="mx-3">
          You are viewing the schedule in read-only mode based on your component policy permissions.
        </Alert>
      )}

      {isPermissionsLoading ? (
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading schedule permissions...</p>
        </div>
      ) : visibleTabs.length === 0 ? (
        <div className="m-3">
          <Alert variant="warning">
            You do not currently have access to any schedule tabs. Contact an administrator to update your component policies.
          </Alert>
        </div>
      ) : (
        <>
          {/* Combined Search and Refresh Section */}
          <div className="schedule-global-actions mx-3 mb-3 d-flex align-items-center gap-3 mt-3">
            <div className="schedule-global-search flex-grow-1">
              <GlobalJobSearch
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                placeholder="Search all jobs by number, name, or model"
              />
            </div>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefreshing || loading}
              className="schedule-refresh-button d-flex align-items-center"
              aria-label="Refresh schedule data"
            >
              {isRefreshing ? (
                <>
                  <div className="spinner-border spinner-border-sm schedule-refresh-spinner me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span className="schedule-refresh-label">Refreshing...</span>
                </>
              ) : (
                <>
                  <i className="bi bi-arrow-repeat p-1"></i>
                  <span className="schedule-refresh-label">Refresh</span>
                </>
              )}
            </Button>
          </div>
          <div className="schedule-content m-3">
            <div className="schedule-tab-selector">
              <label htmlFor="schedule-tab-select">Schedule view</label>
              <select
                id="schedule-tab-select"
                className="form-select"
                value={activeTab}
                onChange={(event) => handleTabChange(event.target.value)}
              >
                {visibleTabs.map((tab) => (
                  <option key={tab.id} value={tab.id}>{tab.label}</option>
                ))}
              </select>
            </div>
            <ul className="schedule-tabs nav nav-tabs mb-4">
              {visibleTabs.map((tab) => (
                <li className="nav-item" key={tab.id}>
                  <button
                    className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                    onClick={() => handleTabChange(tab.id)}
                  >
                    {tab.label}
                  </button>
                </li>
              ))}
            </ul>

            {/* Show loading indicator for all tabs */}
            {showInitialJobScheduleLoading ? (
              <div className="text-center p-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading job data...</p>
              </div>
            ) : error ? (
              <div className="alert alert-danger">Error: {error}</div>
            ) : (
              <div className="tab-content">
                {renderContent()}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default JobScheduleContainer;
